import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SeoService } from '../../core/seo/seo.service';
import { RouterLink } from '@angular/router';
import { switchMap } from 'rxjs';
import { PropertyDataProvider } from '../../core/data/property-data.provider';
import { fmt2, fmtEurPerM2, fmtInt } from '../../core/i18n/labels';
import { Granularity, NationalOverview, SeriesPoint } from '../../core/models/domain.models';
import { lastN } from '../../core/stats/metrics';
import { NamedSeries, SparklineComponent, TrendChartComponent, GaugeChartComponent } from '../../shared/charts.components';
import { PctPipe } from '../../shared/format.pipes';
import {
  DeltaChipComponent,
  GranularityToggleComponent,
  KpiCardComponent,
  MarketNoticeComponent,
  RangeToggleComponent,
} from '../../shared/ui.components';

@Component({
  selector: 'app-home-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    KpiCardComponent,
    MarketNoticeComponent,
    DeltaChipComponent,
    GranularityToggleComponent,
    RangeToggleComponent,
    TrendChartComponent,
    SparklineComponent,
    GaugeChartComponent,
    PctPipe,
  ],
  templateUrl: './home.page.html',
  styleUrl: './home.page.scss',
})
export class HomePage {
  private readonly data = inject(PropertyDataProvider);

  readonly granularity = signal<Granularity>('month');
  readonly rangeMonths = signal<number>(24);
  readonly overview = toSignal(
    toObservable(this.granularity).pipe(switchMap((g) => this.data.overview(g))),
  );

  readonly fmtEurPerM2 = fmtEurPerM2;
  readonly fmtInt = fmtInt;
  readonly fmt2 = fmt2;

  constructor(seo: SeoService) {
    seo.set({
      title: 'Цени на имоти в България 2026 — Имотомер | Анализи, Сделки и Наеми',
      description:
        'Аналитичен барометър на жилищния пазар в България: медианни цени на кв.м, официални сделки от Агенция по вписванията, наеми, доходност и индекс на прегряване в 75 града.',
      path: '/',
      keywords: [
        'цени на имоти българия',
        'пазар на имоти 2026',
        'цени на кв метър',
        'сделки с имоти статистика',
        'агенция по вписванията сделки',
        'наеми по градове',
        'имоти софия пловдив варна бургас',
        'доходност от наем',
        'имотомер',
        'imotomer',
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Имотомер',
        alternateName: 'Imotomer',
        url: 'https://real-estate.tur92.com',
        description:
          'Аналитичен барометър на жилищния пазар в България: цени на кв.м, сделки от Агенция по вписванията, наеми, доходност и индекс на прегряване в 75 града.',
        inLanguage: 'bg',
      },
    });
  }

  ranged(series: SeriesPoint[]): SeriesPoint[] {
    const n = this.rangeMonths();
    return lastN(series, this.granularity() === 'year' ? Math.ceil(n / 12) : n);
  }

  priceChartSeries(o: NationalOverview): NamedSeries[] {
    const base: NamedSeries[] = [
      { name: 'Медиана €/м²', series: this.ranged(o.price.series) },
      { name: 'Сделки / трим.', series: this.ranged(o.transactions.series) },
    ];
    return o.priceForecast.length
      ? [...base, { name: 'Прогноза (линеен тренд)', series: o.priceForecast, projected: true }]
      : base;
  }

  periodLabel(): string {
    const o = this.overview();
    if (!o) return '';
    const s = o.price.series;
    return s.length ? s[s.length - 1].period : '';
  }
}
